import {
  Filter,
  FilterExpression,
  NumberFilter,
  NumberFilterSerialized,
  TextFilter,
  TextFilterSerialized,
  TimeFilter,
  TimeFilterSerialized,
} from '@playq/octopus-common';

import { IBaseSortI } from '/shared/Table/interfaces';

/**
 * T is an entity type, SC - sort type (IBaseSortI<SC>)
 */

type QueryingFilter<T> = (entities: T[], filterBy: Record<string, Filter>) => T[];
type QueryingSort<T, SC extends string> = (entities: T[], sortBy: IBaseSortI<SC>) => T[];
type QueryingEntities<T, SC extends string> = (
  entities: T[],
  filterBy: Record<string, Filter>,
  sortBy: IBaseSortI<SC>
) => T[];

export interface IQuerying<T, SC extends string> {
  filterEntities: QueryingFilter<T>;
  sortEntities: QueryingSort<T, SC>;
  queryEntities: QueryingEntities<T, SC>;
}

export interface IQueryingDefinitions<T> {
  filterField?: string;
  sortField?: string;
  getValue: (e: T) => unknown;
}

/**
 * V is value type
 */
export type FilterFunction<V> = (value: V, filter: Filter) => boolean;

export function textFilter(value: string, filter: TextFilter | TextFilterSerialized): boolean {
  return value.toLowerCase().includes(filter.text.toLowerCase());
}

const EXPRESSIONS_WITH_TIME_SELECT = [FilterExpression.InRange, FilterExpression.OutOfRange];

export function timeFilter(value: Date, filter: TimeFilter | TimeFilterSerialized): boolean {
  const { expression, left, right } = filter;
  const leftComparableValue: Date = typeof left === 'string' ? new Date(left) : left;

  const isFilterDateWithoutTime =
    (value.getHours() === 0 && value.getMinutes() === 0) ||
    !EXPRESSIONS_WITH_TIME_SELECT.includes(expression as FilterExpression);

  if (isFilterDateWithoutTime) {
    value.setHours(0, 0, 0, 0);
    leftComparableValue.setHours(0, 0, 0, 0);
  }

  switch (expression) {
    case FilterExpression.Equal:
      return value.getTime() === leftComparableValue.getTime();
    case FilterExpression.NotEqual:
      return value.getTime() !== leftComparableValue.getTime();
    case FilterExpression.Less:
      return value < leftComparableValue;
    case FilterExpression.LessOrEqual:
      return value <= leftComparableValue;
    case FilterExpression.Greater:
      return value > leftComparableValue;
    case FilterExpression.GreaterOrEqual:
      return value >= leftComparableValue;
  }

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (!right) {
    return false;
  }

  const rightComparableValue = typeof right === 'string' ? new Date(right) : right;

  if (isFilterDateWithoutTime) {
    rightComparableValue.setHours(23, 59, 59, 999);
  }

  switch (expression) {
    case FilterExpression.InRange:
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      return !!rightComparableValue && value > leftComparableValue && value < rightComparableValue;
    case FilterExpression.OutOfRange:
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      return !!rightComparableValue && (value <= leftComparableValue || value >= rightComparableValue);
    default:
      return false;
  }
}

export function numberFilter(value: number, filter: NumberFilter | NumberFilterSerialized): boolean {
  const { expression, left, right } = filter;

  switch (expression) {
    case FilterExpression.Equal:
      return value === left;
    case FilterExpression.NotEqual:
      return value !== left;
    case FilterExpression.Less:
      return value < left;
    case FilterExpression.LessOrEqual:
      return value <= left;
    case FilterExpression.Greater:
      return value > left;
    case FilterExpression.GreaterOrEqual:
      return value >= left;
  }

  if (right === undefined) {
    return false;
  }

  switch (expression) {
    case FilterExpression.InRange:
      return value > left && value < right;
    case FilterExpression.OutOfRange:
      return value <= left || value >= right;
    default:
      return false;
  }
}
